import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Chip
} from '@mui/material';
import {
  Build,
  DragIndicator,
  Palette
} from '@mui/icons-material';
import FormBuilder from '../components/FormBuilder';

const FormBuilderPage = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          py: { xs: 4, md: 6 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Build sx={{ fontSize: 48, mr: 2 }} />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Form Builder
            </Typography>
          </Box>
          <Typography
            variant="h5"
            sx={{
              opacity: 0.9,
              fontWeight: 300,
              maxWidth: 800,
              mx: 'auto',
              mb: 3
            }}
          >
            Create dynamic forms with drag and drop functionality
          </Typography>
          
          {/* Feature chips */}
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<DragIndicator />}
              label="Drag & Drop"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
            />
            <Chip
              icon={<Palette />}
              label="Field Types"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
            />
            <Chip
              label="Export Config"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
            />
            <Chip
              label="Live Preview"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
            />
          </Box>
        </Container>
      </Box>

      {/* Instructions */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            How to use the Form Builder:
          </Typography>
          <Typography variant="body2" component="div">
            • <strong>Drag field types</strong> from the left palette to the form canvas<br/>
            • <strong>Click on fields</strong> in the canvas to edit their properties<br/>
            • <strong>Reorder fields</strong> by dragging them within the canvas<br/>
            • <strong>Export your form</strong> configuration as JSON when finished
          </Typography>
        </Alert>

        {/* Form Builder Component */}
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <FormBuilder />
        </Paper>

        {/* Additional Information */}
        <Paper elevation={1} sx={{ p: 3, mt: 4, borderRadius: 2, bgcolor: 'grey.50' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            Available Field Types
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Chip label="Text Input" variant="outlined" />
            <Chip label="Email Input" variant="outlined" />
            <Chip label="Phone Input" variant="outlined" />
            <Chip label="Dropdown Select" variant="outlined" />
            <Chip label="Date Picker" variant="outlined" />
            <Chip label="Text Area" variant="outlined" />
            <Chip label="Checkbox" variant="outlined" />
            <Chip label="Radio Group" variant="outlined" />
          </Box>
          
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            Features
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            • <strong>Drag and Drop:</strong> Intuitive interface for building forms
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            • <strong>Live Preview:</strong> See how your form will look as you build it
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            • <strong>Field Configuration:</strong> Customize labels, placeholders, validation, and options
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            • <strong>Export Configuration:</strong> Generate JSON configuration for use in applications
          </Typography>
          <Typography variant="body1">
            • <strong>Reordering:</strong> Easily change field order by dragging within the canvas
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default FormBuilderPage;